import { InjectionToken } from '@angular/core';
import { ContractStatus } from '../types/contract.type';
import { OrganizationVersionEnum } from '../enums/organization-version.enum';
import { RouteTo } from './route-to.model';
import { OrganizationDepot } from './organization-depot.model';

/* eslint-disable @typescript-eslint/naming-convention */
export interface ConsumerPortalConfig {
    customerId?: string;
    organizationId: string;
    organizationType: string;
    sourceApp?: string;
    organizationName?: string;
    apiUrl?: string;
    uiUrl?: string;
    loginApiUrl?: string;
    googleMapsApiKey: string;
    googleMapsGeocodeApiUrl: string;
    contractId?: string;
    updatedContractId?: string;
    defaultSubDomain?: string; // Required for dev and local environments
    /** Organization locale (e.g., 'en-GB' for UK, 'en-US' for US) - used for date/time formatting */
    Locale?: string;
    /** All enabled/visible depots (Disabled=0 AND Hidden=0) */
    depots?: OrganizationDepot[];
    showLoader?: boolean;
    menu?: Partial<Menu>;
    header?: Partial<Header>;
    footer?: Partial<Footer>;
    isEmbedded?: boolean;
    consumerPortal?: {
        height: string;
        width: string;
    };
    styling?: {
        themeColors?: {
            primaryColor?: string;
            secondaryColor?: string;
        };
        accountSummary?: Styling;
        contracts?: Styling;
        itemsOut?: Styling;
        tracking?: Styling;
        invoices?: Styling;
        login?: Styling;
        menu?: Styling;
        header?: Styling;
        footer?: Styling;
    };
    Auth?: {
        accessToken?: string;
        AccessToken?: string; // Backward compatibility
        RefreshToken?: string;
        Email?: string;
        OrganizationType?: string;
    };
    /** Payment token for external embedding (replaces Auth) */
    paymentToken?: string;
    enabledFeatures?: EnabledFeatures;
    hiddenFeatures?: string[]; // These are manually hidden features in DB by POR - overrides all
    initialInputConfigAccessToken?: string;
    routeTo?: RouteTo;
    AdImageURL?: {
        Banner1?: string;
        Banner1ExternalUrl?: string;
        Banner2?: string;
        Banner2ExternalUrl?: string;
        Banner3?: string;
        Banner3ExternalUrl?: string;
        Banner4?: string;
        Banner4ExternalUrl?: string;
    };
    licVersion?: OrganizationVersionEnum;
    activeLicenses?: string[];
    storeName?: string;
    payMinimumAmountDueText?: string;
    privacyURL?: string;
    supportEmail?: string;
}
export interface EnabledFeatures {
    essentials?: Features;
    syrinx?: Features;
    expert?: Features;
}

export interface Features {
    invoices?: boolean;
    logOff?: boolean;
    consumerLogin?: boolean;
    multipleCustomerSelection?: boolean;
    payFullAmount?: boolean;
    textSearchMultipleFilters?: boolean; // This Property enables search on the contract page if we enable it will search in other filters else not.
    pdfDownloadFeature?: boolean;
    contractGridJobColumnAvailable?: boolean;
    canSeeBalance?: boolean;
    checkPaymentLicense?: boolean;
    showDetailDocuments?: boolean;
    contractCancel?: boolean;
    contractEdit?: boolean;
    capturePaymentFeature?: boolean;
    showHeaderSections?: boolean;
    canCapturePayment?: ContractStatus[];
    canContractCancel?: ContractStatus[];
    canContractEdit?: ContractStatus[];
    canHaveSelectedItemsActions?: boolean;
    productDetailFeature?: boolean;
    canHaveProductsKeyDocumentsView?: boolean;
    canHaveQuickLinkLoginButton?: boolean;
    displayInspectionDocument?: boolean;
    displayServiceCertificate?: boolean;
    secureUrlLoginRequired?: boolean;
    callOffAbility?: boolean;
    trackingTab?: boolean;
    RequestServiceAbility?: boolean;
    MultiplePaymentAbility?: boolean;
    QuotesPaymentAbility?: boolean;
    amountFieldVisible?: boolean;
    amoutDueFieldVisible?: boolean;
    canSeeContractDetailSiteField?: boolean;
    canSeeContractDetailTotalPaid?: boolean;
    isInvoicePdfDisplayed?: boolean;
    canSeeTrackingTab?: boolean;
    canSeeAmounts?: boolean;
    // ADO-99020: gates the contract-detail Rate column independently of canSeeAmounts (Total), so
    // Syrinx can show a rate without exposing a Total it cannot populate.
    canSeeRates?: boolean;
    canSeeInvoiceDepotCol?: boolean;
    canSeeInvoiceSiteCol?: boolean;
    canSeeInvoiceTypeCol?: boolean;
    canSeeDeliveryAddressColumn?: boolean;
    canSeeInvoiceContractIdCol?: boolean;
    canSeeInvoiceAmountDueCol?: boolean;
    canSeeInvoiceDueDateCol?: boolean;
    canSeeInvoiceBilledDateCol?: boolean;
    canSeeInvoicePOCol?: boolean;
    canSeeInvoiceJobNumberCol?: boolean;
    canSeeInvoiceGrandTaxTotalCol?: boolean;
    canSeeInvoiceDetails?: boolean;
    canSeeInvoiceStatusFilter?: boolean;
    enabledPrivacyPolicy?: boolean;
    invoiceMultiPay?: boolean;
}
export interface Styling {
    paragraph?: [];
    heading?: object[][];
    button?: object[][];
    buttonToggle?: object[][];
    table?: object[][];
    anchor?: object[];
    primaryColor?: [];
    secondaryColor?: [];
    listItem?: [];
}

export interface Menu {
    accountSummary?: boolean;
    contracts?: boolean;
    itemsOut?: boolean;
    tracking?: boolean;
    invoices?: boolean;
}

export interface Header {
    headerTitle: string;
    headerTitlePosition: string;
    logoUrl?: string | null;
    logoPosition: string;
    logoWidth: string;
    faviconUrl: string;
    title?: string | null;
    depotName?: string;
    exterConsumerPortalUrl?: string;
    logoRedirectUrl?: string;
}

export interface Footer {
    footerText: string;
    position: string;
}

export const CONSUMER_PORTAL_APP_CONFIG = new InjectionToken<ConsumerPortalConfig>('Application config');
export const CPEnvironment = new InjectionToken('Consumer Portal Environment config');
